import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { deleteAdminData, getAdminData, patchAdminData } from "../../apis";
import { API } from "../../apis/config";
import LoadingScreen from "../../components/LoadingScreen";
import {
  ParcelOrder,
  ParcelOrderStatus,
  PARCEL_ORDER_STATUS_LABEL,
  normalizeParcelOrderStatus,
} from "../../interfaces/ParcelOrder";
import { downloadOrdersExcel } from "../../components/parcel/downloadOrderExcel";
import { formatKst, utcDate } from "../../utils/utcToKst";

const PAGE_SIZE_OPTIONS = [100, 200, 500, 1000] as const;

const STATUS_BADGE_CLASS: Record<ParcelOrderStatus, string> = {
  PAYMENT_PENDING: "bg-amber-100 text-amber-900 ring-1 ring-amber-200",
  PAYMENT_COMPLETED: "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200",
  SHIPPING_PREPARATION: "bg-sky-100 text-sky-900 ring-1 ring-sky-200",
  SHIPPED: "bg-violet-100 text-violet-900 ring-1 ring-violet-200",
};

function normalizeOrder(row: ParcelOrder & { status?: string }): ParcelOrder {
  const created =
    row.createdAt instanceof Date
      ? new Date(row.createdAt.getTime())
      : utcDate(row.createdAt as unknown as string);
  return {
    ...row,
    createdAt: created,
    status: normalizeParcelOrderStatus(row.status),
  };
}

type ParcelOrdersListQuery = {
  startDate?: string;
  endDate?: string;
  status?: ParcelOrderStatus;
};

type ParcelOrdersPageBody = {
  content: (ParcelOrder & { status?: string })[];
  totalElements: number;
};

function buildOrdersRequestParams(
  query: ParcelOrdersListQuery,
  currentPageOneBased: number,
  pageSize: number,
): Record<string, string> {
  const params: Record<string, string> = {
    page: String(Math.max(0, currentPageOneBased - 1)),
    size: String(pageSize),
  };
  if (query.startDate) params.startDate = query.startDate;
  if (query.endDate) params.endDate = query.endDate;
  if (query.status) params.status = query.status;
  return params;
}

export default function AdminDelivery() {
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState<ParcelOrder[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openOrder, setOpenOrder] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [draftStatus, setDraftStatus] = useState<"" | ParcelOrderStatus>("");
  const [appliedQuery, setAppliedQuery] = useState<ParcelOrdersListQuery>({});
  const [selectedOrders, setSelectedOrders] = useState<
    Record<string, ParcelOrder>
  >({});
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = buildOrdersRequestParams(
        appliedQuery,
        currentPage,
        pageSize,
      );
      const res = await getAdminData<ParcelOrdersPageBody>(API.PARCEL.ORDERS, {
        params,
      });
      if (res.isSuccess && res.result != null) {
        const { content, totalElements } = res.result;
        const list = content.map((row) => normalizeOrder(row));
        const sorted = [...list].sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
        );
        setOrders(sorted);
        setTotalCount(totalElements);
      } else {
        setOrders([]);
        setTotalCount(0);
      }
    } catch (e) {
      console.error(e);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [appliedQuery, currentPage, pageSize]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (totalCount === 0) {
      if (currentPage > 1) setCurrentPage(1);
      return;
    }
    const tp = Math.ceil(totalCount / pageSize);
    if (tp > 0 && currentPage > tp) setCurrentPage(tp);
  }, [totalCount, pageSize, currentPage]);

  const handleSearch = () => {
    const q: ParcelOrdersListQuery = {};
    if (draftStartDate) q.startDate = draftStartDate;
    if (draftEndDate) q.endDate = draftEndDate;
    if (draftStatus) q.status = draftStatus;
    setAppliedQuery(q);
    setCurrentPage(1);
    setSelectedOrders({});
    setOpenOrder(null);
  };

  const handleResetSearch = () => {
    setDraftStartDate("");
    setDraftEndDate("");
    setDraftStatus("");
    setAppliedQuery({});
    setCurrentPage(1);
    setSelectedOrders({});
    setOpenOrder(null);
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 0;

  const pageOrderNumbers = orders.map((o) => o.orderNumber);

  const allOnPageSelected =
    pageOrderNumbers.length > 0 &&
    pageOrderNumbers.every((id) => selectedOrders[id]);
  const someOnPageSelected = pageOrderNumbers.some((id) => selectedOrders[id]);

  useEffect(() => {
    const el = headerCheckboxRef.current;
    if (el) {
      el.indeterminate = someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  const toggleSelectPage = () => {
    setSelectedOrders((prev) => {
      const next = { ...prev };
      if (allOnPageSelected) {
        pageOrderNumbers.forEach((id) => {
          delete next[id];
        });
      } else {
        orders.forEach((o) => {
          next[o.orderNumber] = o;
        });
      }
      return next;
    });
  };

  const toggleSelectRow = (order: ParcelOrder) => {
    setSelectedOrders((prev) => {
      const next = { ...prev };
      if (next[order.orderNumber]) delete next[order.orderNumber];
      else next[order.orderNumber] = order;
      return next;
    });
  };

  const selectedCount = Object.keys(selectedOrders).length;
  const bulkDisabled = selectedCount === 0;

  const handleBulkStatus = async (status: ParcelOrderStatus) => {
    if (bulkDisabled) return;
    const label = PARCEL_ORDER_STATUS_LABEL[status];
    const ok = window.confirm(
      `선택한 ${selectedCount}건의 상태를 「${label}」(으)로 변경할까요?`,
    );
    if (!ok) return;
    const orderNumbers = Object.keys(selectedOrders);
    try {
      await patchAdminData(API.PARCEL.ORDERS_STATUS, {
        orderNumbers,
        status,
      });
      setSelectedOrders({});
      await loadOrders();
    } catch (e) {
      console.error(e);
      alert("상태 변경 중 오류가 발생했습니다.");
    }
  };

  const handleExcelDownload = () => {
    if (selectedCount === 0) {
      alert("다운로드할 주문을 선택해 주세요.");
      return;
    }
    downloadOrdersExcel(Object.values(selectedOrders));
  };

  const generateInvoiceHTML = (order: ParcelOrder) => `
    <html>
      <head>
        <title>거래명세서</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 20px; }
          table { border-collapse: collapse; width: 100%; margin-top: 10px; }
          th, td { border: 1px solid #333; padding: 8px; text-align: left; }
          th { background: #f2f2f2; }
        </style>
      </head>
      <body>
        <h1>거래명세서</h1>
        <p><strong>주문번호:</strong> ${order.orderNumber}</p>
        <p><strong>고객명:</strong> ${order.customerName}</p>
        <p><strong>주소:</strong> ${order.address} ${order.detailAddress}</p>
        <p><strong>연락처:</strong> ${order.phoneNumber}</p>
        <table>
          <thead>
            <tr>
              <th>상품명</th>
              <th>수량</th>
              <th>단가</th>
              <th>금액</th>
            </tr>
          </thead>
          <tbody>
            ${order.parcelProducts
              .map(
                (p) => `
                  <tr>
                    <td>${p.name}</td>
                    <td>${p.quantity}</td>
                    <td>${(p.finalPrice / p.quantity).toLocaleString()}원</td>
                    <td>${p.finalPrice.toLocaleString()}원</td>
                  </tr>
                `,
              )
              .join("")}
          </tbody>
        </table>
        <h3>총합계: ${order.totalPrice.toLocaleString()}원</h3>
      </body>
    </html>
  `;

  const handlePrintInvoice = (order: ParcelOrder) => {
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = generateInvoiceHTML(order);
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleDeleteOrder = async (order: ParcelOrder) => {
    if (
      !window.confirm(
        `주문번호 ${order.orderNumber}을(를) 삭제하시겠습니까?`,
      )
    ) {
      return;
    }

    try {
      await deleteAdminData(API.PARCEL.ORDER, {
        params: { orderNumber: order.orderNumber },
      });

      alert("주문이 삭제되었습니다.");
      setOpenOrder(null);
      setSelectedOrders((prev) => {
        const next = { ...prev };
        delete next[order.orderNumber];
        return next;
      });
      await loadOrders();
    } catch (error) {
      console.error(error);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className="md:mx-2 p-4 space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-4 md:p-6 shadow-sm text-left">
            <div className="flex flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-end xl:gap-2 py-2 mb-4">
              <div className="flex flex-col">
                <label className="text-sm mb-1 text-slate-600">시작일</label>
                <input
                  type="date"
                  min="1900-01-01"
                  max="2999-12-31"
                  value={draftStartDate}
                  onChange={(e) => setDraftStartDate(e.target.value)}
                  className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1 text-slate-600">종료일</label>
                <input
                  type="date"
                  min="1900-01-01"
                  max="2999-12-31"
                  value={draftEndDate}
                  onChange={(e) => setDraftEndDate(e.target.value)}
                  className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                />
              </div>
              <div className="flex flex-col min-w-[8rem]">
                <label className="text-sm mb-1 text-slate-600">상태</label>
                <select
                  value={draftStatus}
                  onChange={(e) =>
                    setDraftStatus(e.target.value as "" | ParcelOrderStatus)
                  }
                  className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
                >
                  <option value="">전체</option>
                  <option value="PAYMENT_PENDING">
                    {PARCEL_ORDER_STATUS_LABEL.PAYMENT_PENDING}
                  </option>
                  <option value="PAYMENT_COMPLETED">
                    {PARCEL_ORDER_STATUS_LABEL.PAYMENT_COMPLETED}
                  </option>
                  <option value="SHIPPING_PREPARATION">
                    {PARCEL_ORDER_STATUS_LABEL.SHIPPING_PREPARATION}
                  </option>
                  <option value="SHIPPED">
                    {PARCEL_ORDER_STATUS_LABEL.SHIPPED}
                  </option>
                </select>
              </div>
              <button
                type="button"
                className="rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 self-start xl:self-auto"
                onClick={handleSearch}
              >
                조회
              </button>
              <button
                type="button"
                className="border border-slate-300 rounded-md px-3 py-2 text-sm hover:bg-slate-50 self-start xl:self-auto"
                onClick={handleResetSearch}
              >
                검색 초기화
              </button>
              <div className="flex flex-wrap gap-2 items-center xl:ml-auto">
                <span className="text-sm text-slate-600 mr-1">
                  선택 {selectedCount}건
                </span>
                <button
                  type="button"
                  disabled={bulkDisabled}
                  onClick={() => handleBulkStatus("PAYMENT_PENDING")}
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-40 disabled:pointer-events-none"
                >
                  결제대기로
                </button>
                <button
                  type="button"
                  disabled={bulkDisabled}
                  onClick={() => handleBulkStatus("PAYMENT_COMPLETED")}
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-40 disabled:pointer-events-none"
                >
                  결제완료로
                </button>
                <button
                  type="button"
                  disabled={bulkDisabled}
                  onClick={() => handleBulkStatus("SHIPPING_PREPARATION")}
                  className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-900 hover:bg-sky-100 disabled:opacity-40 disabled:pointer-events-none"
                >
                  배송준비로
                </button>
                <button
                  type="button"
                  disabled={bulkDisabled}
                  onClick={() => handleBulkStatus("SHIPPED")}
                  className="rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-40 disabled:pointer-events-none"
                >
                  출고완료로
                </button>
                <button
                  type="button"
                  onClick={handleExcelDownload}
                  className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                >
                  엑셀 다운로드
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-700 border-b border-slate-200">
                  <tr>
                    <th className="w-10 px-2 py-3">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300"
                        checked={allOnPageSelected}
                        onChange={toggleSelectPage}
                        aria-label="현재 페이지 전체 선택"
                      />
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      주문번호
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      주문일시
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      고객명
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      연락처
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      상태
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap text-right">
                      금액
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      상세
                    </th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {orders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-12 text-center text-slate-500"
                      >
                        조회 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <Fragment key={order.orderNumber}>
                        <tr className="hover:bg-slate-50/80">
                          <td className="px-2 py-3 align-middle">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300"
                              checked={Boolean(
                                selectedOrders[order.orderNumber],
                              )}
                              onChange={() => toggleSelectRow(order)}
                              aria-label={`주문 ${order.orderNumber} 선택`}
                            />
                          </td>
                          <td className="px-3 py-3 align-middle font-medium text-slate-900 whitespace-nowrap">
                            {order.orderNumber}
                          </td>
                          <td className="px-3 py-3 align-middle text-slate-600 whitespace-nowrap">
                            {formatKst(order.createdAt)}
                          </td>
                          <td className="px-3 py-3 align-middle text-slate-800">
                            {order.customerName}
                          </td>
                          <td className="px-3 py-3 align-middle text-slate-600 whitespace-nowrap">
                            {order.phoneNumber}
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[order.status]}`}
                            >
                              {PARCEL_ORDER_STATUS_LABEL[order.status]}
                            </span>
                          </td>
                          <td className="px-3 py-3 align-middle text-right font-semibold text-slate-900 whitespace-nowrap">
                            {order.totalPrice.toLocaleString()}원
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <button
                              type="button"
                              onClick={() =>
                                setOpenOrder((prev) =>
                                  prev === order.orderNumber
                                    ? null
                                    : order.orderNumber,
                                )
                              }
                              className="text-sky-700 hover:underline text-sm"
                            >
                              {openOrder === order.orderNumber
                                ? "접기"
                                : "펼치기"}
                            </button>
                          </td>
                          <td className="px-3 py-3 align-middle whitespace-nowrap">
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                type="button"
                                onClick={() => handlePrintInvoice(order)}
                                className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                              >
                                거래명세서
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteOrder(order)}
                                className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                              >
                                삭제
                              </button>
                            </div>
                          </td>
                        </tr>
                        {openOrder === order.orderNumber && (
                          <tr className="bg-slate-50/90">
                            <td
                              colSpan={9}
                              className="px-4 py-4 border-t border-slate-100"
                            >
                              <p className="text-sm text-slate-700 mb-3">
                                {order.address} {order.detailAddress}
                              </p>
                              <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden">
                                <thead className="bg-slate-100">
                                  <tr>
                                    <th className="py-2 px-3 border-b border-slate-200 text-left">
                                      상품명
                                    </th>
                                    <th className="py-2 px-3 border-b border-slate-200 text-center w-20">
                                      수량
                                    </th>
                                    <th className="py-2 px-3 border-b border-slate-200 text-right w-28">
                                      금액
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {order.parcelProducts.map((product, idx) => (
                                    <tr key={`${order.orderNumber}-${idx}`}>
                                      <td className="py-2 px-3 border-b border-slate-100">
                                        {product.name}
                                      </td>
                                      <td className="py-2 px-3 border-b border-slate-100 text-center">
                                        {product.quantity}
                                      </td>
                                      <td className="py-2 px-3 border-b border-slate-100 text-right">
                                        {product.finalPrice.toLocaleString()}원
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {totalCount > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6 flex-wrap">
              <span className="text-sm text-slate-600">
                전체 {totalCount.toLocaleString()}건
              </span>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                페이지당 표시
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}건
                    </option>
                  ))}
                </select>
              </label>
              {totalPages > 1 && (
                <div className="flex flex-wrap gap-2 justify-center">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 border rounded-md text-sm ${
                          page === currentPage
                            ? "bg-blue-500 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
